import { useEffect, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Icon } from 'semantic-ui-react';
import WorksheetForm from '~/features/projectWizard/worksheets/common/components/WorksheetForm';
import { WorksheetField } from '../../../worksheets/common/components/Field';
import { InputType } from '~/schema';
import { RevenueUnit, UnitDecimals } from '~/constants';
import { useFadeInStyles, useProjectContext } from '~/hooks';
import { Button } from '~/components';
import Modal from '~/components/Modal';
import { formatNumber } from '~/utils';
import '../System/collapsibleCard.scss';

const defaultAdditionalRevenue = {
  name: '',
  amount: null,
  unit: RevenueUnit.KWH,
  escalator: null,
  expectedTerm: null,
  notes: '',
  startOpen: true,
};

type AdditionalRevenue = {
  name?: string;
  amount?: number | null;
  unit?: string;
  startOpen?: boolean;
};

function revenueHeaderValues(revenue: AdditionalRevenue) {
  const parts: string[] = [];
  if (revenue.amount != null) {
    const isPerYear = revenue.unit === RevenueUnit.YEAR;
    const unitLabel = isPerYear ? 'per year' : 'per kWh';
    const formatted = isPerYear
      ? formatNumber(revenue.amount)
      : formatNumber(revenue.amount, 0, { maxDec: 3 });
    parts.push(`$${formatted} ${unitLabel}`);
  }
  return parts.join(', ');
}

function RevenueSection({
  revenue,
  index,
  total,
  onRemove,
}: {
  revenue: AdditionalRevenue;
  index: number;
  total: number;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(total > 1 ? false : true);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    if (revenue.startOpen) {
      setOpen(true);
      delete revenue.startOpen;
    }
  }, []);

  const headerValues = revenueHeaderValues(revenue);
  const title = revenue.name?.trim()
    ? revenue.name
    : `Revenue stream ${index + 1}`;

  return (
    <section className="collapsible-card" style={useFadeInStyles()}>
      <Collapsible.Root open={open} onOpenChange={() => setOpen(!open)}>
        <div className="collapsible-card__header">
          <Collapsible.Trigger
            type="button"
            aria-label={open ? 'Collapse revenue stream' : 'Expand revenue stream'}
            className="collapsible-card__trigger"
          >
            <span className="collapsible-card__caret">
              <Icon name={open ? 'caret down' : 'caret right'} fitted />
            </span>
            <h3 className="collapsible-card__title">
              {title}{headerValues ? ` - ${headerValues}` : ''}
            </h3>
          </Collapsible.Trigger>
          <div className="collapsible-card__controls">
            <button
              type="button"
              aria-label="Remove revenue stream"
              className="collapsible-card__remove"
              onClick={() => setShowRemoveConfirm(true)}
            >
              <Icon name="trash alternate outline" />
            </button>
          </div>
        </div>
        <Collapsible.Content className="CollapsibleContent">
          <WorksheetForm isInner>
            <WorksheetField
              fieldLabel="Name"
              placeholder="Enter name"
              schemaKey={`additionalRevenues.${index}.name`}
            />
            <WorksheetField
              fieldLabel="Expected annual amount"
              type="number"
              schemaKey={`additionalRevenues.${index}.amount`}
              numberType={InputType.DOLLARS}
              unitField="unit"
              units={[
                { value: RevenueUnit.KWH, label: 'per kWh', decimals: UnitDecimals.KWH },
                { value: RevenueUnit.YEAR, label: 'per year', decimals: UnitDecimals.YEAR },
              ]}
            />
            <WorksheetField
              fieldLabel="Expected annual escalator"
              schemaKey={`additionalRevenues.${index}.escalator`}
              type="number"
              numberType={InputType.PERCENT}
              decimals={2}
              width="half"
            />
            <WorksheetField
              fieldLabel="Expected term"
              schemaKey={`additionalRevenues.${index}.expectedTerm`}
              type="number"
              decimals={0}
              label="years"
              width="half"
            />
            <WorksheetField
              fieldLabel="Notes"
              placeholder="Please describe the revenues provided, particularly the contracted nature of those revenues."
              schemaKey={`additionalRevenues.${index}.notes`}
              type="textarea"
            />
          </WorksheetForm>
        </Collapsible.Content>
      </Collapsible.Root>
      {showRemoveConfirm && (
        <Modal closeModal={() => setShowRemoveConfirm(false)}>
          <Modal.Title>Remove revenue stream {index + 1}?</Modal.Title>
          <Modal.Content>
            <p>
              This will permanently remove this additional revenue stream from the project.
              This action cannot be undone.
            </p>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={() => setShowRemoveConfirm(false)}>
              Cancel
            </Button>
            <Button primary onClick={() => { onRemove(); setShowRemoveConfirm(false); }}>
              Remove revenue stream
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </section>
  );
}

export default function OtherRevenueStreams() {
  const { project, updateProjectValue } = useProjectContext();

  const revenues: AdditionalRevenue[] = project.additionalRevenues ?? [];

  const add = () => {
    updateProjectValue('additionalRevenues', [
      ...revenues,
      { ...defaultAdditionalRevenue },
    ]);
  };

  const remove = (i: number) => {
    updateProjectValue('additionalRevenues', [
      ...revenues.slice(0, i),
      ...revenues.slice(i + 1),
    ]);
  };

  const hasRevenues = revenues.length > 0;

  return (
    <WorksheetForm>
      {hasRevenues ? (
        <>
          {revenues.map((revenue, i) => (
            <RevenueSection
              key={i}
              revenue={revenue}
              index={i}
              total={revenues.length}
              onRemove={() => remove(i)}
            />
          ))}
          <div className="collapsible-card-list-actions">
            <Button variant="link" onClick={add}>
              + Add another revenue stream
            </Button>
          </div>
        </>
      ) : (
        <div className="collapsible-card-empty">
          <p>No additional revenue streams yet</p>
          <Button variant="link" onClick={add}>
            + Add revenue stream
          </Button>
        </div>
      )}
    </WorksheetForm>
  );
}
